import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, Minus, ArrowRight, ShieldCheck, BadgeCheck, Truck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import Seo, { productJsonLd, breadcrumbJsonLd } from "../components/Seo";
import RecentlyViewed from "../components/RecentlyViewed";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { track } = useRecentlyViewed();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [moreFromBrand, setMoreFromBrand] = useState([]);
  const [similarCategory, setSimilarCategory] = useState([]);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    setQty(1);
    (async () => {
      try {
        const r = await api.get(`/products/${id}`);
        setProduct(r.data);
        // Track in recently-viewed once we know the product loaded successfully.
        track(r.data.id);
        // Two parallel queries: more from same brand + similar category.
        const [byBrand, byCat] = await Promise.all([
          r.data.brand
            ? api.get("/products", { params: { brand: r.data.brand, limit: 12 } })
            : Promise.resolve({ data: [] }),
          r.data.category
            ? api.get("/products", { params: { category: r.data.category, limit: 12 } })
            : Promise.resolve({ data: [] }),
        ]);
        const otherIds = new Set([id]);
        const dedupBrand = (byBrand.data || []).filter((p) => !otherIds.has(p.id));
        dedupBrand.slice(0, 4).forEach((p) => otherIds.add(p.id));
        setMoreFromBrand(dedupBrand.slice(0, 4));
        // Exclude the products we already showed in "more from brand" so the
        // two rows don't repeat the same items.
        const dedupCat = (byCat.data || []).filter((p) => !otherIds.has(p.id));
        setSimilarCategory(dedupCat.slice(0, 4));
      } catch (_) {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="pt-32 pb-24 max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-12">
        <div className="col-span-12 md:col-span-7 aspect-[4/5] bg-neutral-200/60 animate-pulse" />
        <div className="col-span-12 md:col-span-5 space-y-6">
          <div className="h-8 bg-neutral-200/60 animate-pulse w-3/4" />
          <div className="h-12 bg-neutral-200/60 animate-pulse w-full" />
          <div className="h-32 bg-neutral-200/60 animate-pulse w-full" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="product-not-found">
        <p className="font-display text-4xl">Piece not found.</p>
        <Link to="/shop" className="btn-primary mt-8 inline-flex">Back to collection</Link>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : ["https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&w=1100&q=80"];

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`Added to bag — ${product.name}`);
  };

  // SEO meta + structured data
  // Avoid "Brand Brand Name" duplication when product name already starts with the brand.
  const seoTitle = product.name.toLowerCase().startsWith(product.brand.toLowerCase())
    ? product.name
    : `${product.brand} ${product.name}`;
  const seoDesc = `${product.brand} ${product.name} — ${formatPrice(product.price)}. ${
    product.description ? product.description.slice(0, 120) : `${product.color || ""} ${product.material || ""} ${product.category.toLowerCase()} bag.`
  }`.slice(0, 220);
  const seoImage = images[0];
  const productSchema = productJsonLd(product);
  const breadcrumbSchema = breadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Shop", url: "/shop" },
    { name: product.brand, url: `/shop?brand=${encodeURIComponent(product.brand)}` },
    { name: product.name },
  ]);
  const combinedSchema = { "@context": "https://schema.org", "@graph": [productSchema, breadcrumbSchema] };

  return (
    <div className="pt-28 pb-24" data-testid="product-detail-page">
      <Seo
        title={seoTitle}
        description={seoDesc}
        image={seoImage}
        type="product"
        jsonLd={combinedSchema}
      />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          {/* Image gallery */}
          <div className="col-span-12 md:col-span-7">
            <div className="aspect-[4/5] overflow-hidden bg-neutral-100 fade-up" data-testid="product-main-image">
              <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-3" data-testid="product-thumbnails">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square overflow-hidden border ${
                      i === activeImg ? "border-[var(--mn-ink)]" : "border-transparent opacity-70"
                    } hover:opacity-100`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="col-span-12 md:col-span-5 fade-up-2 md:sticky md:top-28 self-start">
            <p className="eyebrow">{product.brand}</p>
            <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02] tracking-tighter" data-testid="product-name">
              {product.name}
            </h1>
            <p className="font-mono-luxe text-lg mt-6" data-testid="product-price">{formatPrice(product.price)}</p>

            {product.authenticator && (
              <div
                className="mt-5 inline-flex items-center gap-3 border border-[var(--mn-ink)] px-4 py-2.5"
                data-testid="product-authenticated-badge"
              >
                <BadgeCheck size={16} strokeWidth={1.5} className="shrink-0" />
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] leading-none">Authenticated by</p>
                  <p className="text-sm mt-0.5 leading-tight">
                    {product.authenticator}
                    {product.certificate_number && (
                      <span className="font-mono-luxe text-xs text-[var(--mn-ink-2)] ml-2">
                        · #{product.certificate_number}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-8 space-y-3 text-sm text-[var(--mn-ink-2)] leading-relaxed" data-testid="product-description">
              <p>{product.description || "A curated designer bag from the Elegant Affairs collection — authenticated and shipped worldwide."}</p>
            </div>

            <dl className="mt-8 grid grid-cols-2 gap-y-3 text-xs uppercase tracking-[0.18em]">
              {product.category && (
                <>
                  <dt className="text-[var(--mn-muted)]">Category</dt>
                  <dd>{product.category}</dd>
                </>
              )}
              {product.color && (
                <>
                  <dt className="text-[var(--mn-muted)]">Colour</dt>
                  <dd className="capitalize">{product.color}</dd>
                </>
              )}
              {product.material && (
                <>
                  <dt className="text-[var(--mn-muted)]">Material</dt>
                  <dd>{product.material}</dd>
                </>
              )}
              <dt className="text-[var(--mn-muted)]">Availability</dt>
              <dd className={product.stock > 0 ? "" : "text-[var(--mn-accent)]"}>
                {product.stock > 0 ? `${product.stock} in stock` : "Sold Out"}
              </dd>
            </dl>

            {/* Quantity + Add */}
            <div className="mt-10 flex items-center gap-4">
              <div className="flex items-center border border-[var(--mn-ink)]" data-testid="product-qty-control">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-12 h-14 flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
                  data-testid="product-qty-decrement"
                  aria-label="Decrease"
                >
                  <Minus size={14} strokeWidth={1.5} />
                </button>
                <span className="w-12 text-center font-mono-luxe" data-testid="product-qty-value">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="w-12 h-14 flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
                  data-testid="product-qty-increment"
                  aria-label="Increase"
                >
                  <Plus size={14} strokeWidth={1.5} />
                </button>
              </div>
              <button
                disabled={product.stock <= 0}
                onClick={handleAdd}
                className="btn-primary flex-1 justify-center"
                data-testid="product-add-to-cart"
              >
                {product.stock > 0 ? "Add to Bag" : "Sold Out"}
                {product.stock > 0 && <ArrowRight size={16} strokeWidth={1.5} />}
              </button>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-[var(--mn-border)] pt-8 text-xs">
              <Perk icon={<ShieldCheck size={16} strokeWidth={1.4} />} label="100% Authentic" />
              <Perk icon={<BadgeCheck size={16} strokeWidth={1.4} />} label="Money-back guarantee" />
              <Perk icon={<Truck size={16} strokeWidth={1.4} />} label="Free shipping $200+" />
              <Perk icon={<RotateCcw size={16} strokeWidth={1.4} />} label="14-day returns" />
            </div>

            <Link
              to="/about#concierge"
              className="mt-6 text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-accent)] inline-flex items-center gap-2 border-b border-[var(--mn-border-strong)] pb-1 self-start"
              data-testid="product-concierge-link"
            >
              Need help choosing? Ask us
            </Link>
          </div>
        </div>

        {moreFromBrand.length > 0 && (
          <section className="mt-32" data-testid="more-from-brand-section">
            <div className="flex items-end justify-between mb-12 gap-6">
              <div>
                <p className="eyebrow">From the same house</p>
                <h2 className="font-display text-3xl md:text-5xl mt-3 tracking-tighter">
                  More from <em className="font-medium italic">{product.brand}.</em>
                </h2>
              </div>
              <Link
                to={`/shop?brand=${encodeURIComponent(product.brand)}`}
                className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] underline underline-offset-4 hover:text-[var(--mn-accent)]"
                data-testid="more-from-brand-view-all"
              >
                View all {product.brand}
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {moreFromBrand.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
            <Link
              to={`/shop?brand=${encodeURIComponent(product.brand)}`}
              className="md:hidden mt-8 inline-flex text-xs uppercase tracking-[0.22em] underline underline-offset-4"
            >
              View all {product.brand} →
            </Link>
          </section>
        )}

        {similarCategory.length > 0 && (
          <section className="mt-32" data-testid="similar-category-section">
            <div className="flex items-end justify-between mb-12 gap-6">
              <div>
                <p className="eyebrow">In the same silhouette</p>
                <h2 className="font-display text-3xl md:text-5xl mt-3 tracking-tighter">
                  Similar <em className="font-medium italic">{(product.category || "").toLowerCase()}</em> bags.
                </h2>
              </div>
              <Link
                to={`/shop?category=${encodeURIComponent(product.category)}`}
                className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] underline underline-offset-4 hover:text-[var(--mn-accent)]"
                data-testid="similar-category-view-all"
              >
                Browse all {product.category}
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {similarCategory.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
            <Link
              to={`/shop?category=${encodeURIComponent(product.category)}`}
              className="md:hidden mt-8 inline-flex text-xs uppercase tracking-[0.22em] underline underline-offset-4"
            >
              Browse all {product.category} →
            </Link>
          </section>
        )}

        <RecentlyViewed excludeId={product.id} />
      </div>
    </div>
  );
}

function Perk({ icon, label }) {
  return (
    <div className="flex items-start gap-2 text-[var(--mn-ink-2)]">
      <span className="mt-0.5">{icon}</span>
      <span className="leading-tight">{label}</span>
    </div>
  );
}
